import { StorageStateKeysEnum } from "@utils/storage";
import { observable, action } from "mobx";
import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";
import type { RootStore } from "@state/stores/types";
import { THEME_SETTINGS } from "@theme-settings";
import { COMMON_CONFIGURATION } from "@common-configuration";
import {
	OnboardingNewsletterStore,
	OnboardingRegisterStore,
} from "@state/shared";
import OnboardingNextStore from "@state/shared/OnboardingNextStore";

class OnboardingWelcomePopupStore {
	@observable isActive: boolean = false;
	@observable shouldDisplayOnboardingRegisterPopup: boolean = false;
	@observable shouldDisplayOnboardingNewsletterPopup: boolean = false;
	@observable shouldDisplayOnboardingNextPopup: boolean = false;

	rootStore: RootStore;
	onboardingRegistrationStore: OnboardingRegisterStore;
	onboardingNewsletterStore: OnboardingNewsletterStore;
	onboardingNextStore: OnboardingNextStore;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;

		this.onboardingRegistrationStore = new OnboardingRegisterStore(
			this.rootStore
		);
		this.onboardingNewsletterStore = new OnboardingNewsletterStore(
			this.rootStore
		);
		this.onboardingNextStore = new OnboardingNextStore(this.rootStore);
	}

	@action.bound
	onInitialize() {
		const welcomePopupsDelay = COMMON_CONFIGURATION.WelcomePopupDelay;

		if (
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.ONBOARDING_NEXT_POPUP
			) &&
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.ONBOARDING_REGISTER_POPUP
			) &&
			this.rootStore.localStorageProvider.get(
				StorageStateKeysEnum.ONBOARDING_NEWSLETTER_POPUP
			)
		) {
			return;
		}

		if (THEME_SETTINGS.welcomePopupsEnabled) {
			setTimeout(() => {
				if (
					!this.rootStore.userAuthStore.isLoggedIn &&
					!isCapacitorPlatform()
				) {
					const isNextPopupSeen = JSON.parse(
						this.rootStore.localStorageProvider.get(
							StorageStateKeysEnum.ONBOARDING_NEXT_POPUP
						) as string
					);

					const isRegisterPopupSeen = JSON.parse(
						this.rootStore.localStorageProvider.get(
							StorageStateKeysEnum.ONBOARDING_REGISTER_POPUP
						) as string
					);

					const isNewsletterPopupSeen = JSON.parse(
						this.rootStore.localStorageProvider.get(
							StorageStateKeysEnum.ONBOARDING_NEWSLETTER_POPUP
						) as string
					);

					if (
						THEME_SETTINGS.isOnboardingNextPopupEnabled &&
						!isNextPopupSeen
					) {
						this.shouldDisplayOnboardingNextPopup = true;
					} else if (
						THEME_SETTINGS.isOnboardingRegisterPopupEnabled &&
						!isRegisterPopupSeen
					) {
						this.shouldDisplayOnboardingRegisterPopup = true;
					} else if (
						THEME_SETTINGS.isOnboardingNewsletterPopupEnabled &&
						!isNewsletterPopupSeen
					) {
						this.shouldDisplayOnboardingNewsletterPopup = true;
					}

					this.triggerWelcomePopups();
				}
			}, welcomePopupsDelay);
		}
	}

	triggerWelcomePopups = () => {
		if (!THEME_SETTINGS.welcomePopupsEnabled) return;

		if (
			window.location.pathname.includes("home") ||
			window.location.pathname.includes("sports") ||
			window.location.pathname.includes("live")
		) {
			if (!this.rootStore.userAuthStore.isLoggedIn) {
				if (this.shouldDisplayOnboardingNextPopup) {
					this.onboardingNextStore.showPopup();
					this.shouldDisplayOnboardingNextPopup = false;
				} else if (this.shouldDisplayOnboardingRegisterPopup) {
					this.onboardingRegistrationStore.showPopup();
					this.shouldDisplayOnboardingRegisterPopup = false;
				} else if (this.shouldDisplayOnboardingNewsletterPopup) {
					this.onboardingNewsletterStore.showPopup();
					this.shouldDisplayOnboardingNewsletterPopup = false;
				}
			}
		}
	};

	@action.bound
	showPopup() {
		this.isActive = true;
	}

	@action.bound
	onCancel() {
		this.shouldDisplayOnboardingRegisterPopup = false;
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.ONBOARDING_REGISTER_POPUP,
			JSON.stringify(this.shouldDisplayOnboardingRegisterPopup)
		);
	}

	@action.bound
	onOnboardingRedirection() {
		this.onboardingRegistrationStore.onOnboardingRedirection();
	}
}

export default OnboardingWelcomePopupStore;
